import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Spinner, Alert, Table, Form, Button, Badge, ProgressBar } from 'react-bootstrap';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';
import { dashboardApi } from '../services/api';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const CATEGORY_COLORS = {
  'General Policies': '#6c757d',
  'Board of Trustees Policies': '#0d6efd',
  'Fiscal Policies': '#198754',
  'Human Resources Policies': '#fd7e14',
  'Information Technology Policies': '#6f42c1',
  'Research Policies': '#dc3545',
  'Health & Safety / Emergency Management Policies': '#d63384'
};

const STALENESS_CONFIG = {
  green:   { label: 'Current',      bg: 'success', icon: 'fa-check-circle' },
  yellow:  { label: 'Aging',        bg: 'warning', icon: 'fa-exclamation-circle' },
  red:     { label: 'Needs Review', bg: 'danger',  icon: 'fa-times-circle' },
  unknown: { label: 'No Date',      bg: 'secondary', icon: 'fa-question-circle' },
};

const CAMPUS_META = {
  all: { label: 'All Campuses', shortLabel: 'All', color: '#4f5d75' },
  knoxville: { label: 'Knoxville', shortLabel: 'UTK', color: '#ff8200' },
  martin: { label: 'Martin', shortLabel: 'UTM', color: '#2b59c3' },
  chattanooga: { label: 'Chattanooga', shortLabel: 'UTC', color: '#00a6a6' },
  southern: { label: 'Southern', shortLabel: 'UT Southern', color: '#7a5cff' },
};

// Coordinates projected from actual lat/lon onto viewBox "0 0 640 180"
// x = 20 + (90.5 - lon_W) / 9.3 * 600
// y = 20 + (37.0 - lat_N) / 2.7 * 140
const CAMPUS_MARKERS = [
  {
    key: 'knoxville',
    x: 444, y: 74,
    address: '1331 Circle Park, Knoxville, TN 37996',
    labelDy: 32,
  },
  {
    key: 'chattanooga',
    x: 355, y: 121,
    address: '615 McCallie Ave, Chattanooga, TN 37403',
    labelDy: -24,
  },
  {
    key: 'martin',
    x: 127, y: 54,
    address: '304 Administration Bldg, Martin, TN 38237',
    labelDy: 32,
  },
  {
    key: 'southern',
    x: 244, y: 113,
    address: '433 Madison St, Pulaski, TN 38478',
    labelDy: -24,
  },
];

function getCampusFromFilename(filename) {
  const stem = filename.replace(/\.pdf$/i, '').toUpperCase();
  const match = stem.match(/[A-Z0-9]{6}[_-]([KMCS])(?:[_-]|$)/i);
  if (!match) return null;

  switch (match[1].toUpperCase()) {
    case 'K':
      return 'knoxville';
    case 'M':
      return 'martin';
    case 'C':
      return 'chattanooga';
    case 'S':
      return 'southern';
    default:
      return null;
  }
}

function matchesCampusFilter(filename, campus) {
  if (campus === 'all') return true;
  return getCampusFromFilename(filename) === campus;
}

function buildCampusCounts(pdfCategories) {
  return Object.keys(pdfCategories || {}).reduce((acc, filename) => {
    const campus = getCampusFromFilename(filename);
    if (campus) {
      acc[campus] = (acc[campus] || 0) + 1;
    }
    return acc;
  }, {
    knoxville: 0,
    martin: 0,
    chattanooga: 0,
    southern: 0,
  });
}

function StalenessIndicator({ staleness }) {
  const cfg = STALENESS_CONFIG[staleness] || STALENESS_CONFIG.unknown;
  return (
    <Badge bg={cfg.bg} text={staleness === 'yellow' ? 'dark' : undefined}>
      <i className={`fas ${cfg.icon} me-1`}></i>
      {cfg.label}
    </Badge>
  );
}

function CampusMapWidget({ campusCounts, selectedCampus, onSelectCampus, sortRunLoading, onRunFullSort, sortRunMessage, sortRunError }) {
  const [hoveredCampus, setHoveredCampus] = useState(null);
  return (
    <Card className="mb-3 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #fff7ef 0%, #f5f8ff 100%)' }}>
      <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2 bg-transparent border-0 pb-0">
        <div>
          <div className="fw-semibold">
            <i className="fas fa-map-marked-alt me-2"></i>
            Campus Filter
          </div>
          <small className="text-muted">Click a campus marker to filter the entire dashboard.</small>
        </div>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <Button
            variant={selectedCampus === 'all' ? 'dark' : 'outline-secondary'}
            size="sm"
            onClick={() => onSelectCampus('all')}
          >
            Show All
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={onRunFullSort}
            disabled={sortRunLoading}
          >
            {sortRunLoading ? (
              <><Spinner animation="border" size="sm" className="me-2" />Sorting PDFs…</>
            ) : (
              <><i className="fas fa-shuffle me-2"></i>Run Full PDF Sort</>
            )}
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        {sortRunMessage && <Alert variant="success" className="py-2">{sortRunMessage}</Alert>}
        {sortRunError && <Alert variant="danger" className="py-2">{sortRunError}</Alert>}

        <Row className="g-3 align-items-stretch">
          <Col lg={7}>
            <div className="rounded-4 p-3 h-100" style={{ background: 'rgba(255,255,255,0.75)', border: '1px solid rgba(79, 93, 117, 0.12)' }}>
              {/* Tennessee state outline — points projected from real lat/lon:
                  x = 20 + (90.5 - lon_W) / 9.3 * 600
                  y = 20 + (37.0 - lat_N) / 2.7 * 140  */}
              <svg viewBox="0 0 640 180" width="100%" role="img" aria-label="Tennessee campus map filter">
                <defs>
                  <linearGradient id="tnFill" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#e8f4fd" />
                    <stop offset="100%" stopColor="#d4e8d4" />
                  </linearGradient>
                  <filter id="markerShadow" x="-50%" y="-50%" width="200%" height="200%">
                    <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.25)" />
                  </filter>
                </defs>

                {/* Tennessee state border — simplified outline from actual boundary coordinates */}
                <path
                  d="M 30,46  L 40,38  L 85,36  L 181,37  L 276,36  L 372,36
                     L 472,36  L 536,35  L 591,45
                     L 583,55  L 568,72  L 536,72  L 504,88
                     L 456,96  L 435,118  L 420,125
                     L 323,125  L 168,124  L 115,124
                     L 30,125  Z"
                  fill="url(#tnFill)"
                  stroke="#7aadcc"
                  strokeWidth="2.5"
                  strokeLinejoin="round"
                />

                {/* State label */}
                <text x="200" y="80" textAnchor="middle" fontSize="11" fill="#8aa8c0" fontStyle="italic" opacity="0.7">Tennessee</text>

                {/* Campus markers */}
                {CAMPUS_MARKERS.map((campus) => {
                  const active = selectedCampus === campus.key;
                  const hovered = hoveredCampus === campus.key;
                  const meta = CAMPUS_META[campus.key];
                  const r = active ? 12 : hovered ? 11 : 9;
                  const labelY = campus.y + campus.labelDy;
                  return (
                    <g
                      key={campus.key}
                      onClick={() => onSelectCampus(campus.key)}
                      onMouseEnter={() => setHoveredCampus(campus.key)}
                      onMouseLeave={() => setHoveredCampus(null)}
                      style={{ cursor: 'pointer' }}
                      filter={active || hovered ? 'url(#markerShadow)' : undefined}
                    >
                      {/* Pin circle */}
                      <circle cx={campus.x} cy={campus.y} r={r + 3} fill={meta.color} opacity={active ? 1 : 0.82} />
                      <circle cx={campus.x} cy={campus.y} r={r - 3} fill="#ffffff" />
                      {active && (
                        <circle cx={campus.x} cy={campus.y} r={r + 7} fill="none" stroke={meta.color} strokeWidth="2" opacity="0.4" />
                      )}

                      {/* Label background pill */}
                      <rect
                        x={campus.x - 28} y={labelY - 13}
                        width="56" height="27"
                        rx="6" ry="6"
                        fill={active ? meta.color : 'rgba(255,255,255,0.92)'}
                        stroke={active ? meta.color : 'rgba(79,93,117,0.18)'}
                        strokeWidth="1"
                      />
                      <text
                        x={campus.x} y={labelY + 2}
                        textAnchor="middle" fontSize="10" fontWeight="700"
                        fill={active ? '#ffffff' : '#243447'}
                      >
                        {meta.shortLabel}
                      </text>
                      <text
                        x={campus.x} y={labelY + 13}
                        textAnchor="middle" fontSize="8.5"
                        fill={active ? 'rgba(255,255,255,0.85)' : '#6a7f95'}
                      >
                        {campusCounts[campus.key] || 0} PDFs
                      </text>

                      {/* Hover tooltip with address */}
                      {hovered && (
                        <g>
                          <rect
                            x={Math.min(campus.x - 95, 440)} y={campus.y - 42}
                            width="190" height="34"
                            rx="5" ry="5"
                            fill="#1e2d3d" opacity="0.93"
                          />
                          <text
                            x={Math.min(campus.x - 95, 440) + 95} y={campus.y - 25}
                            textAnchor="middle" fontSize="9" fontWeight="700" fill="#ffffff"
                          >
                            {meta.label}
                          </text>
                          <text
                            x={Math.min(campus.x - 95, 440) + 95} y={campus.y - 14}
                            textAnchor="middle" fontSize="8" fill="#a8c0d8"
                          >
                            {campus.address}
                          </text>
                        </g>
                      )}
                    </g>
                  );
                })}
              </svg>
            </div>
          </Col>
          <Col lg={5}>
            <div className="d-grid gap-2 h-100">
              {Object.entries(CAMPUS_META)
                .filter(([key]) => key !== 'all')
                .map(([key, meta]) => {
                  const active = selectedCampus === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => onSelectCampus(key)}
                      className="btn text-start rounded-4 px-3 py-3"
                      style={{
                        background: active ? meta.color : 'rgba(255,255,255,0.7)',
                        color: active ? '#fff' : '#233243',
                        border: `1px solid ${active ? meta.color : 'rgba(79, 93, 117, 0.16)'}`,
                        boxShadow: active ? '0 12px 24px rgba(35, 50, 67, 0.18)' : 'none',
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-semibold">{meta.label}</span>
                        <Badge bg={active ? 'light' : 'secondary'} text={active ? 'dark' : undefined}>
                          {campusCounts[key] || 0}
                        </Badge>
                      </div>
                      <small style={{ opacity: active ? 0.92 : 0.72 }}>
                        {active ? 'Dashboard filtered to this campus' : 'Apply campus-only dashboard view'}
                      </small>
                    </button>
                  );
                })}
              <div className="small text-muted mt-2">
                System-wide PDFs stay visible only when <strong>All Campuses</strong> is selected.
              </div>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}

function DataDashboardTab() {
  const [categoryData, setCategoryData] = useState(null);
  const [selectedCampus, setSelectedCampus] = useState('all');
  const [oldPdfs, setOldPdfs] = useState(null);
  const [thresholdYears, setThresholdYears] = useState(10);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [oldPdfsLoading, setOldPdfsLoading] = useState(true);
  const [categoryError, setCategoryError] = useState(null);
  const [oldPdfsError, setOldPdfsError] = useState(null);

  // Staleness state
  const [stalenessData, setStalenessData] = useState(null);
  const [stalenessLoading, setStalenessLoading] = useState(true);
  const [stalenessError, setStalenessError] = useState(null);
  const [revisionYear, setRevisionYear] = useState(2020);

  // Version tracker state
  const [versionSearch, setVersionSearch] = useState('');
  const [versionSort, setVersionSort] = useState({ col: 'revision_number', dir: 'desc' });

  // Similarity state
  const [selectedPdf, setSelectedPdf] = useState('');
  const [similarityResults, setSimilarityResults] = useState(null);
  const [similarityLoading, setSimilarityLoading] = useState(false);
  const [similarityError, setSimilarityError] = useState(null);

  // Category policies state
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryPolicies, setCategoryPolicies] = useState([]);

  // Edit category state
  const [checkedPolicies, setCheckedPolicies] = useState(new Set());
  const [bulkNewCategory, setBulkNewCategory] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState(null);
  const [sortRunLoading, setSortRunLoading] = useState(false);
  const [sortRunMessage, setSortRunMessage] = useState(null);
  const [sortRunError, setSortRunError] = useState(null);

  const loadCategories = useCallback(async () => {
    try {
      setCategoryLoading(true);
      setCategoryError(null);
      const data = await dashboardApi.getCategories();
      setCategoryData(data);
    } catch (err) {
      setCategoryError(err.response?.data?.detail || 'Failed to load category data');
    } finally {
      setCategoryLoading(false);
    }
  }, []);

  const loadOldPdfs = useCallback(async (years) => {
    try {
      setOldPdfsLoading(true);
      setOldPdfsError(null);
      const data = await dashboardApi.getOldPDFs(years);
      setOldPdfs(data);
    } catch (err) {
      setOldPdfsError(err.response?.data?.detail || 'Failed to load old PDFs data');
    } finally {
      setOldPdfsLoading(false);
    }
  }, []);

  const loadStaleness = useCallback(async () => {
    try {
      setStalenessLoading(true);
      setStalenessError(null);
      const data = await dashboardApi.getStaleness();
      setStalenessData(data);
    } catch (err) {
      setStalenessError(err.response?.data?.detail || 'Failed to load staleness data');
    } finally {
      setStalenessLoading(false);
    }
  }, []);

  const filteredPdfCategories = categoryData
    ? Object.fromEntries(
        Object.entries(categoryData.pdf_categories).filter(([filename]) => matchesCampusFilter(filename, selectedCampus))
      )
    : null;

  const filteredCategoryCounts = filteredPdfCategories
    ? Object.values(filteredPdfCategories).reduce((acc, category) => {
        acc[category] = (acc[category] || 0) + 1;
        return acc;
      }, {})
    : {};

  const campusCounts = categoryData ? buildCampusCounts(categoryData.pdf_categories) : buildCampusCounts({});
  const filteredTotalPdfs = filteredPdfCategories ? Object.keys(filteredPdfCategories).length : 0;
  const filteredOldPdfRows = oldPdfs ? oldPdfs.old_pdfs.filter((pdf) => matchesCampusFilter(pdf.filename, selectedCampus)) : [];
  const filteredStalenessPolicies = stalenessData
    ? stalenessData.policies.filter((policy) => matchesCampusFilter(policy.filename, selectedCampus))
    : [];
  const filteredStalenessCounts = filteredStalenessPolicies.reduce((acc, policy) => {
    acc[policy.staleness] = (acc[policy.staleness] || 0) + 1;
    return acc;
  }, { green: 0, yellow: 0, red: 0, unknown: 0 });
  const filteredYearDistribution = filteredStalenessPolicies.reduce((acc, policy) => {
    if (policy.effective_year) {
      acc[policy.effective_year] = (acc[policy.effective_year] || 0) + 1;
    }
    return acc;
  }, {});

  const handleRunFullSort = async () => {
    try {
      setSortRunLoading(true);
      setSortRunError(null);
      setSortRunMessage(null);
      const result = await dashboardApi.runFullPdfSort();
      setSortRunMessage(`Sorted ${result.processed} PDFs. Moved ${result.moved}, left ${result.unchanged} in place, skipped ${result.skipped}.`);
      await Promise.all([
        loadCategories(),
        loadOldPdfs(thresholdYears),
        loadStaleness(),
      ]);
    } catch (err) {
      setSortRunError(err.response?.data?.detail || 'Failed to run full PDF sort');
    } finally {
      setSortRunLoading(false);
    }
  };

  const handleCategoryClick = (category) => {
    if (filteredPdfCategories) {
      const policies = Object.entries(filteredPdfCategories)
        .filter(([, cat]) => cat === category)
        .map(([filename]) => filename)
        .sort((a, b) => a.localeCompare(b));
      setSelectedCategory(category);
      setCategoryPolicies(policies);
      setCheckedPolicies(new Set());
      setBulkNewCategory('');
      setEditError(null);
    }
  };

  useEffect(() => {
    loadCategories();
    loadOldPdfs(thresholdYears);
    loadStaleness();
  }, [loadCategories, loadOldPdfs, loadStaleness, thresholdYears]);

  useEffect(() => {
    if (!selectedCategory) return;

    const policies = Object.entries(filteredPdfCategories || {})
      .filter(([, cat]) => cat === selectedCategory)
      .map(([filename]) => filename)
      .sort((a, b) => a.localeCompare(b));

    if (policies.length === 0) {
      setSelectedCategory(null);
      setCategoryPolicies([]);
      setCheckedPolicies(new Set());
      setBulkNewCategory('');
      return;
    }

    setCategoryPolicies(policies);
  }, [selectedCategory, filteredPdfCategories]);

  const handleSimilaritySearch = async () => {
    if (!selectedPdf) return;
    try {
      setSimilarityLoading(true);
      setSimilarityError(null);
      setSimilarityResults(null);
      const data = await dashboardApi.getSimilarity(selectedPdf);
      setSimilarityResults(data);
    } catch (err) {
      setSimilarityError(err.response?.data?.detail || 'Failed to compute similarity');
    } finally {
      setSimilarityLoading(false);
    }
  };

  const handleOpenEditModal = (policyFilename) => {
    setCheckedPolicies(new Set([policyFilename]));
    setBulkNewCategory(categoryData?.pdf_categories[policyFilename] || '');
    setEditError(null);
  };

  const handleToggleCheck = (filename) => {
    setCheckedPolicies(prev => {
      const next = new Set(prev);
      if (next.has(filename)) next.delete(filename);
      else next.add(filename);
      return next;
    });
  };

  const handleToggleAll = () => {
    if (checkedPolicies.size === categoryPolicies.length) {
      setCheckedPolicies(new Set());
    } else {
      setCheckedPolicies(new Set(categoryPolicies));
    }
  };

  const handleBulkSave = async () => {
    if (checkedPolicies.size === 0 || !bulkNewCategory) return;
    try {
      setEditLoading(true);
      setEditError(null);
      await dashboardApi.bulkUpdatePolicyCategories([...checkedPolicies], bulkNewCategory);
      await loadCategories();
      setCheckedPolicies(new Set());
      setBulkNewCategory('');
    } catch (err) {
      setEditError(err.response?.data?.detail || 'Failed to update categories');
    } finally {
      setEditLoading(false);
    }
  };

  const handleResetCategory = async (filename) => {
    try {
      setEditLoading(true);
      setEditError(null);
      await dashboardApi.resetPolicyCategory(filename);
      await loadCategories();
    } catch (err) {
      setEditError(err.response?.data?.detail || 'Failed to reset category');
    } finally {
      setEditLoading(false);
    }
  };

  // Build chart data from category counts
  const chartData = filteredTotalPdfs > 0 ? {
    labels: Object.keys(filteredCategoryCounts),
    datasets: [{
      data: Object.values(filteredCategoryCounts),
      backgroundColor: Object.keys(filteredCategoryCounts).map(
        cat => CATEGORY_COLORS[cat] || '#999'
      ),
      borderColor: '#fff',
      borderWidth: 2
    }]
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          padding: 15,
          usePointStyle: true,
          pointStyle: 'circle',
          font: { size: 12 }
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const pct = ((context.raw / total) * 100).toFixed(1);
            return ` ${context.label}: ${context.raw} PDFs (${pct}%)`;
          }
        }
      }
    }
  };

  // Age distribution bar chart data
  const barChartData = filteredStalenessPolicies.length > 0 ? (() => {
    const years = Object.keys(filteredYearDistribution).map(Number).sort((a, b) => a - b);
    const BAR_COLORS = years.map(y => {
      const age = new Date().getFullYear() - y;
      if (age < 2) return '#198754';
      if (age < 5) return '#ffc107';
      return '#dc3545';
    });
    return {
      labels: years,
      datasets: [{
        label: 'Policies',
        data: years.map(y => filteredYearDistribution[y]),
        backgroundColor: BAR_COLORS,
        borderRadius: 4,
      }]
    };
  })() : null;

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.raw} policies (effective ${ctx.label})`,
        }
      }
    },
    scales: {
      x: { title: { display: true, text: 'Effective Year' } },
      y: { title: { display: true, text: 'Policy Count' }, beginAtZero: true, ticks: { stepSize: 10 } }
    }
  };

  // Needs revision list
  const needsRevisionList = filteredStalenessPolicies
    ? filteredStalenessPolicies.filter(p => p.effective_year && p.effective_year <= revisionYear)
    : [];

  // Version tracker list — all campus-filtered policies, searched + sorted
  const filteredVersionPolicies = (() => {
    let list = filteredStalenessPolicies;
    if (versionSearch.trim()) {
      const q = versionSearch.trim().toLowerCase();
      list = list.filter(p =>
        p.filename.toLowerCase().includes(q) ||
        (p.revision_number && p.revision_number.toLowerCase().includes(q))
      );
    }
    return [...list].sort((a, b) => {
      const { col, dir } = versionSort;
      let av, bv;
      if (col === 'revision_number') {
        av = a.revision_number != null ? parseInt(a.revision_number, 10) : -1;
        bv = b.revision_number != null ? parseInt(b.revision_number, 10) : -1;
      } else if (col === 'age_years') {
        av = a.age_years ?? -1;
        bv = b.age_years ?? -1;
      } else if (col === 'effective_date') {
        av = a.effective_date ? new Date(a.effective_date).getTime() : 0;
        bv = b.effective_date ? new Date(b.effective_date).getTime() : 0;
      } else {
        av = a.filename.toLowerCase();
        bv = b.filename.toLowerCase();
      }
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ? 1 : -1;
      return 0;
    });
  })();

  const handleVersionSort = (col) => {
    setVersionSort(prev => ({ col, dir: prev.col === col && prev.dir === 'desc' ? 'asc' : 'desc' }));
  };

  const versionSortIcon = (col) => {
    if (versionSort.col !== col) return <i className="fas fa-sort ms-1 text-muted opacity-50" />;
    return versionSort.dir === 'asc'
      ? <i className="fas fa-sort-up ms-1 text-primary" />
      : <i className="fas fa-sort-down ms-1 text-primary" />;
  };


  // Get sorted list of all PDF filenames for the dropdown
  const allPdfFilenames = filteredPdfCategories
    ? Object.keys(filteredPdfCategories).sort((a, b) => a.localeCompare(b))
    : [];

  return (
    <div className="p-3">
      <h4 className="mb-3">
        <i className="fas fa-chart-pie me-2"></i>
        Data Dashboard
      </h4>

      <CampusMapWidget
        campusCounts={campusCounts}
        selectedCampus={selectedCampus}
        onSelectCampus={setSelectedCampus}
        sortRunLoading={sortRunLoading}
        onRunFullSort={handleRunFullSort}
        sortRunMessage={sortRunMessage}
        sortRunError={sortRunError}
      />

      <Row className="g-3">
        {/* Category Distribution Chart */}
        <Col lg={7}>
          <Card className="h-100">
            <Card.Header>
              <i className="fas fa-chart-pie me-2"></i>
              PDF Category Distribution
              {categoryData && (
                <>
                  <Badge bg="secondary" className="ms-2">{filteredTotalPdfs} PDFs</Badge>
                  {selectedCampus !== 'all' && (
                    <Badge bg="dark" className="ms-2">{CAMPUS_META[selectedCampus].label}</Badge>
                  )}
                </>
              )}
            </Card.Header>
            <Card.Body>
              {categoryLoading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" /><br />
                  <small className="text-muted mt-2">Loading categories...</small>
                </div>
              ) : categoryError ? (
                <Alert variant="danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {categoryError}
                  <Button variant="outline-danger" size="sm" className="ms-2" onClick={loadCategories}>
                    Retry
                  </Button>
                </Alert>
              ) : chartData ? (
                <div style={{ height: '350px' }}>
                  <Doughnut data={chartData} options={chartOptions} />
                </div>
              ) : (
                <Alert variant="secondary" className="mb-0">
                  No PDFs match the current campus filter.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Category Breakdown Table */}
        <Col lg={5}>
          <Card className="h-100">
            <Card.Header>
              <i className="fas fa-list me-2"></i>
              Category Breakdown
            </Card.Header>
            <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {categoryLoading ? (
                <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
              ) : categoryError ? null : categoryData && filteredTotalPdfs > 0 ? (
                <Table size="sm" hover>
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th className="text-end">Count</th>
                      <th style={{ width: '30%' }}>Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(filteredCategoryCounts)
                      .sort(([, a], [, b]) => b - a)
                      .map(([category, count]) => {
                        const pct = ((count / filteredTotalPdfs) * 100).toFixed(1);
                        const isSelected = selectedCategory === category;
                        return (
                          <tr 
                            key={category}
                            onClick={() => handleCategoryClick(category)}
                            style={{
                              cursor: 'pointer',
                              backgroundColor: isSelected ? 'rgba(13, 110, 253, 0.1)' : 'inherit',
                              transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (!isSelected) e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) e.currentTarget.style.backgroundColor = 'inherit';
                            }}
                          >
                            <td>
                              <span
                                className="d-inline-block me-2"
                                style={{
                                  width: 12, height: 12, borderRadius: '50%',
                                  backgroundColor: CATEGORY_COLORS[category] || '#999'
                                }}
                              />
                              <span className={isSelected ? 'fw-bold' : ''}>
                                {category.replace(' Policies', '')}
                              </span>
                              {isSelected && (
                                <i className="fas fa-check-circle ms-2 text-primary" style={{ fontSize: '0.8em' }}></i>
                              )}
                            </td>
                            <td className="text-end fw-bold">{count}</td>
                            <td>
                              <ProgressBar
                                now={parseFloat(pct)}
                                style={{ height: 8 }}
                                variant="info"
                              />
                              <small className="text-muted">{pct}%</small>
                            </td>
                          </tr>
                        );
                      })}
                  </tbody>
                </Table>
              ) : (
                <div className="text-muted small">No categories for the selected campus.</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Category Policies Section */}
      {selectedCategory && (
        <Row className="g-3 mt-1">
          <Col xs={12}>
            <Card>
              <Card.Header className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <span>
                  <i className="fas fa-folder-open me-2"></i>
                  Policies in <strong>{selectedCategory}</strong>
                  <Badge bg="secondary" className="ms-2">{categoryPolicies.length}</Badge>
                  {selectedCampus !== 'all' && <Badge bg="dark" className="ms-2">{CAMPUS_META[selectedCampus].label}</Badge>}
                </span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => {
                    setSelectedCategory(null);
                    setCategoryPolicies([]);
                    setCheckedPolicies(new Set());
                    setBulkNewCategory('');
                    setEditError(null);
                  }}
                >
                  <i className="fas fa-times me-1"></i>
                  Close
                </Button>
              </Card.Header>

              {/* Bulk Action Toolbar — visible when at least one row checked */}
              {checkedPolicies.size > 0 && (
                <div
                  className="d-flex align-items-center gap-2 px-3 py-2 flex-wrap"
                  style={{ background: 'rgba(13, 110, 253, 0.08)', borderBottom: '1px solid rgba(13, 110, 253, 0.2)' }}
                >
                  <Badge bg="primary" className="me-1">{checkedPolicies.size} selected</Badge>
                  <span className="text-muted small">Move to:</span>
                  <Form.Select
                    size="sm"
                    style={{ width: 'auto', minWidth: 220 }}
                    value={bulkNewCategory}
                    onChange={(e) => setBulkNewCategory(e.target.value)}
                    disabled={editLoading}
                  >
                    <option value="">-- Select category --</option>
                    {categoryData?.available_categories?.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </Form.Select>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleBulkSave}
                    disabled={editLoading || !bulkNewCategory}
                  >
                    {editLoading
                      ? <><Spinner animation="border" size="sm" className="me-1" />Saving…</>
                      : <><i className="fas fa-save me-1"></i>Apply</>
                    }
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setCheckedPolicies(new Set())}
                    disabled={editLoading}
                  >
                    Clear
                  </Button>
                  {editError && (
                    <span className="text-danger small ms-2">
                      <i className="fas fa-exclamation-circle me-1"></i>{editError}
                    </span>
                  )}
                </div>
              )}

              <Card.Body className="p-0">
                {categoryPolicies.length === 0 ? (
                  <div className="p-3">
                    <Alert variant="info" className="mb-0">
                      <i className="fas fa-info-circle me-2"></i>
                      No policies found in this category.
                    </Alert>
                  </div>
                ) : (
                  <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
                    <Table size="sm" hover className="mb-0">
                      <thead className="sticky-top bg-white">
                        <tr>
                          <th style={{ width: '42px' }} className="text-center">
                            <Form.Check
                              type="checkbox"
                              checked={checkedPolicies.size === categoryPolicies.length && categoryPolicies.length > 0}
                              ref={(el) => {
                                if (el) el.indeterminate = checkedPolicies.size > 0 && checkedPolicies.size < categoryPolicies.length;
                              }}
                              onChange={handleToggleAll}
                              title="Select all"
                            />
                          </th>
                          <th>#</th>
                          <th>Filename</th>
                          <th style={{ width: '60px' }} className="text-center">Reset</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categoryPolicies.map((policy, idx) => {
                          const checked = checkedPolicies.has(policy);
                          return (
                            <tr
                              key={policy}
                              onClick={() => handleToggleCheck(policy)}
                              style={{
                                cursor: 'pointer',
                                backgroundColor: checked ? 'rgba(13, 110, 253, 0.08)' : undefined
                              }}
                            >
                              <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                <Form.Check
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => handleToggleCheck(policy)}
                                />
                              </td>
                              <td className="text-muted">
                                <small>{idx + 1}</small>
                              </td>
                              <td>
                                <i className="fas fa-file-pdf text-danger me-2"></i>
                                <span className="font-monospace small">{policy}</span>
                              </td>
                              <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={() => handleResetCategory(policy)}
                                  disabled={editLoading}
                                  title="Reset to auto-detection"
                                  style={{ padding: '0.2rem 0.45rem', fontSize: '0.75rem' }}
                                >
                                  <i className="fas fa-undo"></i>
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
              <Card.Footer className="bg-light text-muted small d-flex justify-content-between align-items-center">
                <span>
                  <i className="fas fa-list me-1"></i>
                  <strong>{categoryPolicies.length}</strong> policies · click rows or use checkbox to select
                </span>
                {checkedPolicies.size > 0 && (
                  <span className="text-primary fw-semibold">{checkedPolicies.size} selected</span>
                )}
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      )}

      {/* Staleness Summary + Age Distribution Chart */}
      <Row className="g-3 mt-1">
        {/* Staleness summary chips */}
        <Col lg={4}>
          <Card className="h-100">
            <Card.Header>
              <i className="fas fa-clock me-2"></i>
              Policy Freshness Summary
            </Card.Header>
            <Card.Body>
              {stalenessLoading ? (
                <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
              ) : stalenessError ? (
                <Alert variant="danger" className="py-2">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {stalenessError}
                  <Button variant="link" size="sm" onClick={loadStaleness}>Retry</Button>
                </Alert>
              ) : stalenessData ? (
                <>
                  {[
                    { key: 'green',   label: 'Current',        sub: '< 2 years old',  bg: '#198754', icon: 'fa-check-circle' },
                    { key: 'yellow',  label: 'Aging',          sub: '2–5 years old',  bg: '#ffc107', icon: 'fa-exclamation-circle', dark: true },
                    { key: 'red',     label: 'Needs Revision', sub: '5+ years old',   bg: '#dc3545', icon: 'fa-times-circle' },
                    { key: 'unknown', label: 'No Date',        sub: 'Date not found', bg: '#6c757d', icon: 'fa-question-circle' },
                  ].map(({ key, label, sub, bg, icon, dark }) => (
                    <div
                      key={key}
                      className="d-flex align-items-center rounded px-3 py-2 mb-2"
                      style={{ background: bg, color: dark ? '#212529' : '#fff' }}
                    >
                      <i className={`fas ${icon} fa-lg me-3`}></i>
                      <div className="flex-grow-1">
                        <div className="fw-bold">{label}</div>
                        <div className="small opacity-75">{sub}</div>
                      </div>
                      <span className="fs-4 fw-bold">{filteredStalenessCounts[key] || 0}</span>
                    </div>
                  ))}
                </>
              ) : null}
            </Card.Body>
          </Card>
        </Col>

        {/* Age distribution bar chart */}
        <Col lg={8}>
          <Card className="h-100">
            <Card.Header>
              <i className="fas fa-chart-bar me-2"></i>
              Policy Age Distribution by Effective Year
              <span className="ms-2 small text-muted fw-normal">
                (green &lt;2 yr · yellow 2–5 yr · red 5+ yr)
              </span>
            </Card.Header>
            <Card.Body>
              {stalenessLoading ? (
                <div className="text-center py-5"><Spinner animation="border" /></div>
              ) : stalenessError ? null : barChartData ? (
                <div style={{ height: '260px' }}>
                  <Bar data={barChartData} options={barChartOptions} />
                </div>
              ) : null}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Needs Revision Filter */}
      <Row className="g-3 mt-1">
        <Col xs={12}>
          <Card>
            <Card.Header className="d-flex align-items-center flex-wrap gap-2">
              <span>
                <i className="fas fa-calendar-exclamation me-2"></i>
                Needs Revision — Policies Not Updated Since
              </span>
              <div className="d-flex align-items-center gap-2 ms-auto">
                <input
                  type="range"
                  min={2000}
                  max={new Date().getFullYear() - 1}
                  step={1}
                  value={revisionYear}
                  onChange={e => setRevisionYear(Number(e.target.value))}
                  style={{ width: 160, accentColor: 'var(--ut-orange)' }}
                />
                <small style={{ minWidth: 60, fontWeight: 600 }}>≤ {revisionYear}</small>
                {stalenessData && (
                  <Badge bg={needsRevisionList.length > 0 ? 'danger' : 'success'}>
                    {needsRevisionList.length} policies
                  </Badge>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              {stalenessLoading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2 text-muted">Loading…</span>
                </div>
              ) : stalenessError ? (
                <Alert variant="danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {stalenessError}
                </Alert>
              ) : needsRevisionList.length === 0 ? (
                <Alert variant="success" className="mb-0">
                  <i className="fas fa-check-circle me-2"></i>
                  No policies found with effective dates on or before {revisionYear}.
                </Alert>
              ) : (
                <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                  <Table size="sm" hover>
                    <thead>
                      <tr>
                        <th>Filename</th>
                        <th>Effective Date</th>
                        <th className="text-end">Age</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {needsRevisionList.map(p => (
                        <tr key={p.filename}>
                          <td>
                            <i className="fas fa-file-pdf text-danger me-1"></i>
                            {p.filename}
                          </td>
                          <td>{p.effective_date || '—'}</td>
                          <td className="text-end">{p.age_years != null ? `${p.age_years} yrs` : '—'}</td>
                          <td>
                            <StalenessIndicator staleness={p.staleness} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Old PDFs Section */}
      <Row className="g-3 mt-1">
        <Col xs={12}>
          <Card>
            <Card.Header className="d-flex align-items-center flex-wrap gap-2">
              <span>
                <i className="fas fa-calendar-times me-2"></i>
                PDFs Needing Revision
              </span>
              <div className="d-flex align-items-center gap-2 ms-auto">
                <small className="text-muted">Threshold:</small>
                <input
                  type="range"
                  min={1}
                  max={20}
                  step={1}
                  value={thresholdYears}
                  onChange={(e) => setThresholdYears(Number(e.target.value))}
                  style={{ width: 120, accentColor: 'var(--ut-orange)' }}
                />
                <small style={{ minWidth: 60, fontWeight: 600 }}>{thresholdYears}+ yrs</small>
                {oldPdfs && (
                  <Badge bg={filteredOldPdfRows.length > 0 ? 'warning' : 'success'}>
                    {filteredOldPdfRows.length} found
                  </Badge>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              {oldPdfsLoading ? (
                <div className="text-center py-3">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2 text-muted">Checking PDF dates...</span>
                </div>
              ) : oldPdfsError ? (
                <Alert variant="danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {oldPdfsError}
                  <Button variant="outline-danger" size="sm" className="ms-2" onClick={() => loadOldPdfs(thresholdYears)}>
                    Retry
                  </Button>
                </Alert>
              ) : oldPdfs && filteredOldPdfRows.length === 0 ? (
                <Alert variant="success" className="mb-0">
                  <i className="fas fa-check-circle me-2"></i>
                  No PDFs in the current campus filter need revision under the {thresholdYears}-year threshold.
                </Alert>
              ) : oldPdfs ? (
                <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                  <Table size="sm" hover>
                    <thead>
                      <tr>
                        <th>Filename</th>
                        <th>Last Modified</th>
                        <th className="text-end">Age (Years)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOldPdfRows.map((pdf) => (
                        <tr key={pdf.filename}>
                          <td>
                            <i className="fas fa-file-pdf text-danger me-1"></i>
                            {pdf.filename}
                          </td>
                          <td>{new Date(pdf.last_modified).toLocaleDateString()}</td>
                          <td className="text-end">
                            <Badge bg={pdf.age_years >= 15 ? 'danger' : 'warning'}>
                              {pdf.age_years} yrs
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : null}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ── Version / Revision Tracker ───────────────────────────────── */}
      <Row className="g-3 mt-1">
        <Col xs={12}>
          <Card>
            <Card.Header className="d-flex align-items-center flex-wrap gap-2">
              <span>
                <i className="fas fa-code-branch me-2"></i>
                Policy Version Tracker
                {stalenessData && (
                  <>
                    <Badge bg="primary" className="ms-2">
                      {filteredStalenessPolicies.filter(p => p.revision_number != null).length} tracked
                    </Badge>
                    <Badge bg="secondary" className="ms-2">
                      {filteredStalenessPolicies.filter(p => p.revision_number == null).length} untracked
                    </Badge>
                    {selectedCampus !== 'all' && (
                      <Badge bg="dark" className="ms-2">{CAMPUS_META[selectedCampus].label}</Badge>
                    )}
                  </>
                )}
              </span>
              <div className="ms-auto d-flex gap-2 align-items-center flex-wrap">
                <Form.Control
                  size="sm"
                  placeholder="Search policies…"
                  value={versionSearch}
                  onChange={e => setVersionSearch(e.target.value)}
                  style={{ width: 200 }}
                />
                {versionSearch && (
                  <Button variant="outline-secondary" size="sm" onClick={() => setVersionSearch('')}>
                    <i className="fas fa-times" />
                  </Button>
                )}
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {stalenessLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2 text-muted">Loading…</span>
                </div>
              ) : stalenessError ? (
                <Alert variant="danger" className="m-3">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {stalenessError}
                </Alert>
              ) : filteredVersionPolicies.length === 0 ? (
                <div className="text-center text-muted py-4">
                  <i className="fas fa-search fa-2x mb-2 d-block opacity-50"></i>
                  No policies match your search.
                </div>
              ) : (
                <div style={{ maxHeight: '480px', overflowY: 'auto' }}>
                  <Table size="sm" hover className="mb-0">
                    <thead className="sticky-top bg-white">
                      <tr>
                        <th
                          onClick={() => handleVersionSort('filename')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                          Policy {versionSortIcon('filename')}
                        </th>
                        <th
                          onClick={() => handleVersionSort('revision_number')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                          Rev # {versionSortIcon('revision_number')}
                        </th>
                        <th
                          onClick={() => handleVersionSort('effective_date')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                          Effective Date {versionSortIcon('effective_date')}
                        </th>
                        <th
                          className="text-end"
                          onClick={() => handleVersionSort('age_years')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                        >
                          Age {versionSortIcon('age_years')}
                        </th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredVersionPolicies.map(p => (
                        <tr key={p.filename}>
                          <td>
                            <i className="fas fa-file-pdf text-danger me-1" />
                            <span className="font-monospace small">{p.filename}</span>
                          </td>
                          <td>
                            {p.revision_number != null ? (
                              <Badge
                                bg={parseInt(p.revision_number, 10) >= 5 ? 'warning' : 'info'}
                                text={parseInt(p.revision_number, 10) >= 5 ? 'dark' : undefined}
                                className="font-monospace"
                              >
                                <i className="fas fa-tag me-1" />
                                Rev {p.revision_number}
                              </Badge>
                            ) : (
                              <span className="text-muted small">—</span>
                            )}
                          </td>
                          <td>{p.effective_date || <span className="text-muted">—</span>}</td>
                          <td className="text-end">
                            {p.age_years != null
                              ? <span className={p.age_years >= 5 ? 'text-danger fw-semibold' : p.age_years >= 2 ? 'text-warning' : 'text-success'}>{p.age_years} yrs</span>
                              : <span className="text-muted">—</span>
                            }
                          </td>
                          <td><StalenessIndicator staleness={p.staleness} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
            {stalenessData && (
              <Card.Footer className="bg-light text-muted small d-flex justify-content-between align-items-center flex-wrap gap-1">
                <span>
                  <i className="fas fa-info-circle me-1" />
                  Revision numbers are extracted from the <strong>Revision History</strong> table in each policy's OCR markdown cache.
                </span>
                <span>
                  Showing <strong>{filteredVersionPolicies.length}</strong> of {filteredStalenessPolicies.length} policies
                </span>
              </Card.Footer>
            )}
          </Card>
        </Col>
      </Row>

      {/* Similarity Comparison Tool */}
      <Row className="g-3 mt-1">
        <Col xs={12}>
          <Card>
            <Card.Header>
              <i className="fas fa-exchange-alt me-2"></i>
              PDF Cosine Similarity Comparison
            </Card.Header>
            <Card.Body>
              <Row className="align-items-end mb-3">
                <Col md={8}>
                  <Form.Label>Select a PDF to find similar documents</Form.Label>
                  <Form.Select
                    value={selectedPdf}
                    onChange={(e) => setSelectedPdf(e.target.value)}
                    disabled={similarityLoading || allPdfFilenames.length === 0}
                  >
                    <option value="">
                      {allPdfFilenames.length === 0
                        ? 'Loading PDF list...'
                        : '-- Select a PDF --'}
                    </option>
                    {allPdfFilenames.map((fn) => (
                      <option key={fn} value={fn}>{fn}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Button
                    variant="primary"
                    onClick={handleSimilaritySearch}
                    disabled={!selectedPdf || similarityLoading}
                    className="w-100"
                  >
                    {similarityLoading ? (
                      <><Spinner animation="border" size="sm" className="me-2" />Computing...</>
                    ) : (
                      <><i className="fas fa-search me-2"></i>Find Similar PDFs</>
                    )}
                  </Button>
                </Col>
              </Row>

              {similarityError && (
                <Alert variant="danger">
                  <i className="fas fa-exclamation-triangle me-2"></i>
                  {similarityError}
                </Alert>
              )}

              {similarityResults && (
                <>
                  <Alert variant="info" className="py-2">
                    <i className="fas fa-info-circle me-2"></i>
                    Showing top {similarityResults.similar_pdfs.length} most similar documents
                    to <strong>{similarityResults.source_pdf}</strong>
                    {' '}(out of {similarityResults.total_compared} compared)
                  </Alert>
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <Table size="sm" hover>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Filename</th>
                          <th className="text-end">Cosine Similarity</th>
                          <th style={{ width: '25%' }}>Match Strength</th>
                        </tr>
                      </thead>
                      <tbody>
                        {similarityResults.similar_pdfs.map((item, idx) => {
                          const pct = (item.similarity * 100).toFixed(1);
                          let variant = 'danger';
                          if (item.similarity >= 0.85) variant = 'success';
                          else if (item.similarity >= 0.7) variant = 'info';
                          else if (item.similarity >= 0.5) variant = 'warning';
                          return (
                            <tr key={item.filename}>
                              <td className="text-muted">{idx + 1}</td>
                              <td>
                                <i className="fas fa-file-pdf text-danger me-1"></i>
                                {item.filename}
                              </td>
                              <td className="text-end fw-bold">{item.similarity.toFixed(4)}</td>
                              <td>
                                <ProgressBar
                                  now={parseFloat(pct)}
                                  variant={variant}
                                  style={{ height: 8 }}
                                />
                                <small className="text-muted">{pct}%</small>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}

              {!similarityResults && !similarityError && !similarityLoading && (
                <div className="text-center text-muted py-4">
                  <i className="fas fa-exchange-alt fa-2x mb-2 d-block"></i>
                  Select a PDF and click "Find Similar PDFs" to compare document embeddings using cosine similarity.
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default DataDashboardTab;
